import { useEffect } from "react";
import { clsx } from "clsx";
import { format } from "date-fns";
import { Button } from "@/ui/components";
import { Icon } from "@/ui/icons/Icon";
import { PageLoader } from "@/layouts/components/PageLoader/PageLoader";
import { PhoneStatusIcons } from "@/assets/images/loan";
import { externalUrls } from "@/constants/urls";
import { CreditProductType } from "@/features/offer/types";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useMediaQuery } from "@/hooks";
import { useUser } from "@/features/user/hooks/useUser";
import { formatAmountWithSpaces } from "@/lib/amountUtils";
import { getNextPaymentInfo } from "@/lib/loans.utility";
import { useLoan } from "../../hooks/useLoan";
import { ConfettiBurst } from "../../components/ConfettiBurst";
import styles from "./ActiveLoanPage.module.scss";

const DAY_MS = 24 * 60 * 60 * 1000;

const splitAmount = (amount: number | undefined) => {
  const [integer, decimal] = (amount ?? 0).toFixed(2).split(".");
  return {
    integer: formatAmountWithSpaces(Number(integer), 0) || "0",
    decimal: `,${decimal} lei`,
  };
};

interface PhoneLoanCardProps {
  isCreditLine: boolean;
  amount: number | undefined;
  dueDate: Date | undefined;
}

// The credit as the app shows it, drawn inside the phone.
function PhoneLoanCard({ isCreditLine, amount, dueDate }: PhoneLoanCardProps) {
  const { integer, decimal } = splitAmount(amount);

  return (
    <div className={styles.loanCard}>
      <div
        className={clsx(
          styles.productCard,
          isCreditLine
            ? styles["productCard--creditLine"]
            : styles["productCard--installment"],
        )}
      >
        <p className={styles.productTitle}>
          {isCreditLine ? "Linie de credit" : "Credit în rate"}
        </p>
        <div className={styles.productBottom}>
          <div className={styles.productAmount}>
            <p className={styles.productLabel}>
              {isCreditLine ? "Disponibil" : "De rambursat"}
            </p>
            <p className={styles.productValue}>
              <span className={styles.productInteger}>{integer}</span>
              <span className={styles.productDecimal}>{decimal}</span>
            </p>
          </div>
          {!isCreditLine && dueDate && (
            <div className={styles.productDue}>
              <p className={styles.productLabel}>Scadența</p>
              <p className={styles.productDueDate}>
                {format(new Date(dueDate), "dd.MM.yy")}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className={styles.loanActions}>
        {isCreditLine ? (
          <>
            <span
              className={clsx(styles.loanAction, styles["loanAction--filled"])}
            >
              <Icon name="arrow-down-right" size={16} />
              Cash
            </span>
            <span
              className={clsx(styles.loanAction, styles["loanAction--tonal"])}
            >
              <Icon name="arrow-up-left" size={16} />
              Rambursează
            </span>
          </>
        ) : (
          <>
            <span
              className={clsx(styles.loanAction, styles["loanAction--tonal"])}
            >
              <Icon name="time-forward" size={16} />
              Amână plata
            </span>
            <span
              className={clsx(styles.loanAction, styles["loanAction--filled"])}
            >
              <Icon name="arrow-up-left" size={16} />
              Rambursează
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function ActiveLoanPage() {
  const {
    activeLoanSummary,
    isLoadingCurrentContract,
    currentContract,
    getCurrentContract,
  } = useLoan(true);
  const { requestAppDownloadLink } = useAuth();
  const { isDesktop } = useMediaQuery();
  const { user } = useUser();

  useEffect(() => {
    getCurrentContract();
  }, []);

  if (isLoadingCurrentContract) {
    return <PageLoader />;
  }

  const handleDownloadApp = () => {
    if (isDesktop && user) {
      requestAppDownloadLink({ phone: user.phone });
    }

    window.location.href = externalUrls.volt.appDownload;
  };

  const contract = currentContract?.contract;
  const productType = contract?.productType ?? activeLoanSummary?.productType;
  const isCreditLine =
    productType === CreditProductType.CreditLine ||
    productType === CreditProductType.WhiteLabelCard;

  // Signed today, or just now (the contract has no signing date yet).
  const isTodayCredit =
    !contract?.signedAt ||
    new Date(contract.signedAt).getTime() >= Date.now() - DAY_MS;

  const title = !isTodayCredit ? (
    <>
      Ai deja un
      <br />
      contract semnat
    </>
  ) : isCreditLine ? (
    <>
      Felicitări! Contractul
      <br />e semnat 🎉
    </>
  ) : (
    <>
      Felicitări! Banii au fost
      <br />
      trimiși pe card. 🎉
    </>
  );

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {isTodayCredit && <ConfettiBurst className={styles.confetti} />}

        <div className={styles.phoneArea} aria-hidden>
          <div className={styles.phone}>
            <div className={styles.phoneBezel}>
              <div className={styles.phoneScreen}>
                <div className={styles.statusBar}>
                  <span className={styles.statusTime}>9:41</span>
                  <span className={styles.dynamicIsland} />
                  <PhoneStatusIcons className={styles.statusIcons} />
                </div>
                <div className={styles.loanCardFrame}>
                  <PhoneLoanCard
                    isCreditLine={isCreditLine}
                    amount={
                      isCreditLine
                        ? activeLoanSummary?.availableAmount
                        : activeLoanSummary?.totalAmount
                    }
                    dueDate={getNextPaymentInfo(contract)?.date}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className={styles.banner}>
          <h1 className={styles.title}>{title}</h1>
          <ul className={styles.usps}>
            <li className={styles.usp}>
              <Icon name="download-alt" size={32} className={styles.uspIcon} />
              <p className={styles.uspText}>
                Descarcă
                <br />
                Volt
              </p>
            </li>
            <li className={styles.usp}>
              <Icon name="coins" size={32} className={styles.uspIcon} />
              <p className={styles.uspText}>
                {isCreditLine ? "Retrage" : "Gestionează"}
                <br />
                {isCreditLine ? "banii" : "creditul"}
              </p>
            </li>
          </ul>
        </section>
      </div>

      <div className={styles.bottomSection}>
        <Button
          trackingId="active_loan_download_volt_app"
          rightIcon={<Icon name="arrow-right" />}
          className={styles.downloadButton}
          text="Descarcă aplicația Volt"
          onClick={handleDownloadApp}
        />
      </div>
    </div>
  );
}

export default ActiveLoanPage;
